import { type RouteSectionProps, useParams } from "@solidjs/router";
import type { Channel } from "sdk";
import type { JSX, ParentProps } from "solid-js";
import { createEffect, createMemo, Match, on, Show, Switch } from "solid-js";
import { createStore } from "solid-js/store";
import { useApi, useChannels } from "@/api";
import { useCtx } from "@/app/context";
import icX from "@/assets/x-1.png";
import { Icon } from "@/atoms/Icon";
import { Resizable } from "@/atoms/Resizable.tsx";
import { DocumentProvider } from "@/components/document/context";
import { Document } from "@/components/document/Document.tsx";
import { DocumentHistory } from "@/components/document/DocumentHistory";
import { Wiki } from "@/components/document/Wiki";
import { Calendar } from "@/components/features/calendar/Calendar";
import { ChatMain } from "@/components/features/chat/Chat.tsx";
import { ChatHeader } from "@/components/features/chat/ChatHeader.tsx";
import { PinnedMessages } from "@/components/features/chat/PinnedMessages.tsx";
import { SearchResults } from "@/components/features/chat/SearchResults.tsx";
import { ThreadMembers } from "@/components/features/chat/Thread.tsx";
import { Forum } from "@/components/features/forum/Forum";
import {
	Forum2,
	Forum2Thread,
	Forum2ThreadPage,
} from "@/components/features/forum/Forum2";
import { Scripts } from "@/components/features/scripts/Scripts";
import { useVoice } from "@/components/features/voice/context.tsx";
import { Voice } from "@/components/features/voice/Voice.tsx";
import { Category } from "@/components/shared/Category";
import { ChannelNav } from "@/components/shared/ChannelNav";
import { Friends } from "@/components/shared/Friends";
import { Home } from "@/components/shared/Home";
import { Inbox } from "@/components/shared/Inbox";
import { RoomHome, RoomMembers } from "@/components/shared/Room";
import { RoomHeader } from "@/components/shared/RoomHeader";
import { RoomNav } from "@/components/shared/RoomNav";
import { RoomTemplatePage } from "@/components/shared/RoomTemplatePage";
import { SearchPage } from "@/components/shared/SearchPage";
import { Title } from "@/components/shared/Title";
import { UserTray } from "@/components/shared/UserTray";
import {
	ChannelContext,
	createInitialChannelState,
	useChannel,
} from "@/contexts/channel";
import { useCurrentUser } from "@/contexts/currentUser.tsx";
import { createInitialRoomState, RoomContext } from "@/contexts/room.tsx";
import { useSearch } from "@/contexts/search";
import { useCurrentRoomId } from "@/hooks/useCurrentRoomId";
import { flags } from "@/lib/flags";
import { MediaSidebar } from "@/media/Sidebar";
import type { RoomT } from "@/types";

export { RouteInvite } from "@/components/shared/Invite";
export { RouteAuthorize } from "@/components/shared/Oauth";
export { RouteUser } from "@/components/shared/UserPage";

export const AppLayoutMain = (props: ParentProps<RouteSectionProps>) => {
	const roomId = useCurrentRoomId();
	const params = useParams();

	return (
		<>
			<Resizable
				storageKey="nav-tray-width"
				side="left"
				// 64px room nav + 256 channel nav
				initialWidth={320}
				// TODO: don't have magic numbers
				minWidth={244}
				maxWidth={564}
			>
				<div class="nav-tray">
					<RoomNav />
					<ChannelNav room_id={roomId() ?? ""} view_id={params.view_id} />
					<UserTray />
				</div>
			</Resizable>
			{props.children}
		</>
	);
};

const RoomSidebar = (props: { room: RoomT }) => {
	const ctx = useCtx();
	const { states } = useSearch();
	const search = () => states[props.room.id];

	const showMembers = () =>
		flags.has("room_member_list") &&
		ctx.preferences().frontend.showMembers !== false;

	return (
		<Switch>
			<Match when={search()}>
				<Resizable storageKey="search-sidebar-width" initialWidth={320}>
					<SearchResults room={props.room} search={search()} />
				</Resizable>
			</Match>
			<Match when={showMembers()}>
				<Resizable
					storageKey="room-members-width"
					initialWidth={198}
					minWidth={180}
					maxWidth={500}
				>
					<RoomMembers room={props.room} />
				</Resizable>
			</Match>
		</Switch>
	);
};

export const RouteRoom = (p: ParentProps<RouteSectionProps>): JSX.Element => {
	const { t } = useCtx();
	const ctx = useCtx();
	const api = useApi();
	const room = api.rooms.use(() => p.params.room_id);

	const getOrCreateRoomContext = () => {
		const roomId = p.params.room_id;
		if (!roomId) return null;

		if (!ctx.room_contexts.has(roomId)) {
			const store = createStore(createInitialRoomState());
			ctx.room_contexts.set(roomId, store);
		}

		return ctx.room_contexts.get(roomId)!;
	};

	const roomCtx = getOrCreateRoomContext();

	if (api.streams) {
		createEffect(
			on(
				() => p.params.room_id,
				(room_id) => {
					if (!room_id) return;

					api.streams.subscribeRoom(room_id, (_sync) => {
						// TODO: handle sync
					});
				},
			),
		);
	}

	return (
		<Show when={roomCtx} fallback={<div>Loading room...</div>}>
			<RoomContext.Provider value={roomCtx!}>
				<Title title={room() ? room()?.name : t("loading")} />
				<Show when={room()}>
					<RoomHeader room={room()!} />
					<RoomHome room={room()!} />
					<RoomSidebar room={room()!} />
				</Show>
			</RoomContext.Provider>
		</Show>
	);
};

export const RouteRoomTemplate = RoomTemplatePage;

const ThreadChatSidebar = (props: { thread_id: string }) => {
	const channels2 = useChannels();
	const thread = channels2.use(() => props.thread_id);
	const ctx = useCtx();
	const [_ch, setChannelState] = useChannel()!;

	const getOrCreateChannelContext = () => {
		const channelId = props.thread_id;
		if (!channelId) return null;

		if (!ctx.channel_contexts.has(channelId)) {
			const store = createStore(createInitialChannelState());
			ctx.channel_contexts.set(channelId, store);
		}

		return ctx.channel_contexts.get(channelId)!;
	};

	const channelCtx = createMemo(() => getOrCreateChannelContext());

	const onClose = () => {
		setChannelState("thread_chat_sidebar_thread_id", undefined);
	};

	return (
		<div class="thread-chat-sidebar">
			<Show when={thread()}>
				{(t) => (
					<Show when={channelCtx()}>
						{(cc) => (
							<ChannelContext.Provider value={cc()}>
								<button type="button" class="close" onClick={onClose}>
									<Icon src={icX} />
								</button>
								<Switch>
									<Match when={t().type === "ThreadForum2"}>
										<Forum2Thread channel={t()} />
									</Match>
									<Match when={true}>
										<ChatMain channel={t()} />
									</Match>
								</Switch>
							</ChannelContext.Provider>
						)}
					</Show>
				)}
			</Show>
		</div>
	);
};

const DocumentSidebar = (props: { thread_id: string }) => {
	const channels2 = useChannels();
	const thread = channels2.use(() => props.thread_id);
	const ctx = useCtx();
	const [_ch, setChannelState] = useChannel()!;

	const getOrCreateChannelContext = () => {
		const channelId = props.thread_id;
		if (!channelId) return null;

		if (!ctx.channel_contexts.has(channelId)) {
			const store = createStore(createInitialChannelState());
			ctx.channel_contexts.set(channelId, store);
		}

		return ctx.channel_contexts.get(channelId)!;
	};

	const channelCtx = createMemo(() => getOrCreateChannelContext());

	const onClose = () => {
		setChannelState("sidebar_document_id", undefined);
	};

	return (
		<div class="thread-chat-sidebar">
			<Show when={thread()}>
				{(t) => (
					<Show when={channelCtx()}>
						{(cc) => (
							<ChannelContext.Provider value={cc()}>
								<button type="button" class="close" onClick={onClose}>
									<Icon src={icX} />
								</button>
								<DocumentProvider initialBranchId={t().id}>
									<Document channel={t()} />
								</DocumentProvider>
							</ChannelContext.Provider>
						)}
					</Show>
				)}
			</Show>
		</div>
	);
};

const ChannelSidebar = (props: { channel: Channel }) => {
	const ctx = useCtx();
	const [ch] = useChannel()!;
	const [voice] = useVoice();
	const { states } = useSearch();
	const search = () => states[props.channel.id];
	const showMembers = () =>
		props.channel.type !== "Voice" &&
		props.channel.type !== "Broadcast" &&
		flags.has("channel_member_list") &&
		ctx.preferences().frontend.showMembers !== false;
	const showPinned = () => ch.pinned_view ?? false;
	const inDmVoice = () =>
		(props.channel.type === "Dm" || props.channel.type === "Gdm") &&
		voice.joinedChannelId === props.channel.id;
	const showVoiceChat = () =>
		(props.channel.type === "Voice" ||
			props.channel.type === "Broadcast" ||
			inDmVoice()) &&
		ch.voice_chat_sidebar_open;
	const showHistory = () =>
		props.channel.type === "Document" && ch.history_view;
	const showDocumentSidebar = () => ch.sidebar_document_id;
	const showThreadChatSidebar = () => ch.thread_chat_sidebar_thread_id;

	// TODO: make document sidebar and thread sidebar use different resizable storage keys

	return (
		<Switch>
			<Match when={showDocumentSidebar()}>
				<Resizable
					storageKey="document-sidebar-width"
					initialWidth={400}
					minWidth={300}
					maxWidth={600}
				>
					<DocumentSidebar thread_id={ch.sidebar_document_id!} />
				</Resizable>
			</Match>
			<Match when={showThreadChatSidebar()}>
				<Resizable
					storageKey="thread-chat-sidebar-width"
					initialWidth={400}
					minWidth={300}
					maxWidth={600}
				>
					<ThreadChatSidebar thread_id={ch.thread_chat_sidebar_thread_id!} />
				</Resizable>
			</Match>
			<Match when={showHistory()}>
				<Resizable storageKey="document-history-width" initialWidth={320}>
					<DocumentHistory channel={props.channel} />
				</Resizable>
			</Match>
			<Match when={showVoiceChat()}>
				<Resizable storageKey="voice-chat-sidebar-width" initialWidth={320}>
					<div class="voice-chat-sidebar">
						<ChatMain channel={props.channel} />
					</div>
				</Resizable>
			</Match>
			<Match when={search()}>
				<Resizable storageKey="search-sidebar-width" initialWidth={320}>
					<SearchResults channel={props.channel} search={search()} />
				</Resizable>
			</Match>
			<Match when={showPinned()}>
				<Resizable storageKey="pinned-sidebar-width" initialWidth={320}>
					<PinnedMessages channel={props.channel} />
				</Resizable>
			</Match>
			<Match when={showMembers()}>
				<Resizable
					storageKey="thread-members-width"
					initialWidth={198}
					minWidth={180}
					maxWidth={500}
				>
					<ThreadMembers thread={props.channel} />
				</Resizable>
			</Match>
			<Match when={ch.sidebar_media}>
				{(media) => (
					<Resizable
						storageKey="media-width"
						initialWidth={400}
						minWidth={300}
						maxWidth={600}
					>
						<MediaSidebar media={media()} />
					</Resizable>
				)}
			</Match>
		</Switch>
	);
};

export const RouteChannel = (
	p: ParentProps<RouteSectionProps>,
): JSX.Element => {
	const { t } = useCtx();
	const ctx = useCtx();
	const api = useApi();
	const channel = api.channels.use(() => p.params.channel_id);
	const room = api.rooms.use(() => channel()?.room_id!);
	const [voice] = useVoice();

	const getOrCreateChannelContext = () => {
		const channelId = p.params.channel_id;
		if (!channelId) return null;

		if (!ctx.channel_contexts.has(channelId)) {
			const store = createStore(createInitialChannelState());
			ctx.channel_contexts.set(channelId, store);
		}

		return ctx.channel_contexts.get(channelId)!;
	};

	const channelCtx = createMemo(() => getOrCreateChannelContext());

	// store last viewed channel per room
	createEffect(() => {
		const ch = channel();
		const rm = room();
		if (ch?.room_id && rm) {
			const key = `last_channel_${rm.id}`;
			localStorage.setItem(key, ch.id);
		}
	});

	// Handle message anchor logic
	createEffect(() => {
		const { channel_id, message_id, script_id } = p.params;
		const c = channelCtx();
		if (!c) return;

		const [channelState, setChannelState] = c;

		if (channel_id && message_id) {
			channelState.timelineState.controller.jumpToMessage(
				message_id,
				false,
				true,
			);
		}
		if (channel_id && script_id) {
			setChannelState("script_id", script_id);
		}
	});

	const currentUser = useCurrentUser();
	const title = () => {
		const ch = channel();
		if (!ch) return t("loading");
		if (ch.type === "Dm") {
			const user_id = currentUser()?.id;
			return ch.recipients?.find((i) => i.id !== user_id)?.name ?? "dm";
		}

		return room() && ch.room_id ? `${ch.name} - ${room()?.name}` : ch.name;
	};

	if (api.streams) {
		createEffect(
			on(channel, (channel) => {
				if (!channel) return;

				const room_id = channel.room_id;
				if (room_id) {
					api.streams.subscribeRoom(room_id, (_sync) => {
						// TODO: handle sync
					});
				}

				api.streams.subscribeChannel(channel.id, (_sync) => {
					// TODO: handle sync
				});
			}),
		);
	}

	return (
		<Show when={channelCtx()} fallback={<div>Loading channel...</div>}>
			{(cc) => (
				<ChannelContext.Provider value={cc()}>
					<Title title={title()} />
					<Show when={channel()}>
						{(ch) => (
							<>
								<Switch>
									<Match
										when={ch().type === "Voice" || ch().type === "Broadcast"}
									>
										<Voice channel={ch()} />
									</Match>
									<Match when={ch().type === "Dm" || ch().type === "Gdm"}>
										<Show
											when={voice.joinedChannelId === ch().id}
											fallback={
												<>
													<ChatHeader channel={ch()} />
													<ChatMain channel={ch()} />
												</>
											}
										>
											<Voice channel={ch()} />
										</Show>
									</Match>
									<Match
										when={
											ch().type === "Text" ||
											ch().type === "Announcement" ||
											ch().type === "ThreadPublic" ||
											ch().type === "ThreadPrivate"
										}
									>
										<ChatHeader channel={ch()} />
										<ChatMain channel={ch()} />
									</Match>
									<Match when={ch().type === "Document"}>
										<DocumentProvider initialBranchId={ch().id}>
											<Document channel={ch()} />
											<ChannelSidebar channel={ch()} />
										</DocumentProvider>
									</Match>
									<Match when={ch().type === "Wiki"}>
										<ChatHeader channel={ch()} />
										<Wiki channel={ch()} />
									</Match>
									<Match when={ch().type === "Forum"}>
										<ChatHeader channel={ch()} />
										<Forum channel={ch()} />
									</Match>
									<Match when={ch().type === "Ticket"}>
										<ChatHeader channel={ch()} />
										<Forum channel={ch()} />
									</Match>
									<Match when={ch().type === "Forum2"}>
										<ChatHeader channel={ch()} />
										<Forum2 channel={ch()} />
									</Match>
									<Match when={ch().type === "ThreadForum2"}>
										<ChatHeader channel={ch()} />
										<Forum2ThreadPage channel={ch()} />
									</Match>
									<Match when={ch().type === "Calendar"}>
										<Calendar channel={ch()} />
									</Match>
									<Match when={ch().type === "Scripts"}>
										<Scripts channel={ch()} />
									</Match>
									<Match when={ch().type === "Category"}>
										<Category channel={ch()} />
									</Match>
								</Switch>
								<Show when={ch().type !== "Document"}>
									<ChannelSidebar channel={ch()} />
								</Show>
							</>
						)}
					</Show>
				</ChannelContext.Provider>
			)}
		</Show>
	);
};

export const RouteHome = (
	_props: ParentProps<RouteSectionProps>,
): JSX.Element => {
	const { t } = useCtx();
	return (
		<>
			<Title title={t("page.home")} />
			<Home />
		</>
	);
};

export function RouteInbox(_p: RouteSectionProps): JSX.Element {
	return (
		<>
			<Title title="inbox" />
			<Inbox />
		</>
	);
}

export function RouteFriends(): JSX.Element {
	return (
		<>
			<Title title="friends" />
			<Friends />
		</>
	);
}

export function RouteNotFound(): JSX.Element {
	const { t } = useCtx();

	return (
		<>
			<Title title="not found" />
			<div style="padding:8px">{t("not_found")}</div>
		</>
	);
}

export function RouteSearch(): JSX.Element {
	const { t } = useCtx();

	return (
		<>
			<Title title={t("page.search")} />
			<SearchPage />
		</>
	);
}
